import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, NgZone, Input, ElementRef, AfterViewInit, HostListener } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { filter } from 'rxjs/operators';
import { ScrollDispatcher } from '@angular/cdk/scrolling';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DragulaService } from 'ng2-dragula';

import { AppComponent } from '../../../app.component';
import { IoService } from '../../../services/io/io.service';
import { ReportService } from '../../../services/report/report.service';
import { AuthorizationService } from '../../../services/authorization/authorization.service';
import { LocationService } from '../../../services/location/location.service';
import { TagPrintService } from '../../../services/tag-print/tag-print.service';
import { LoadingComponent } from '../../shared/loading/loading.component';
import { ListBuilderService } from '../../../services/list-builder/list-builder.service';
import { ClientSettingsService } from '../../../services/client-settings/client-settings.service';
import { ResponsiveService } from '../../../services/responsive/responsive.service';
import { FilterDateRangeComponent } from '../filter-date-range/filter-date-range.component';
import { DateRange, DateRangeSettings } from '../filter-date-range/date-config-interface';
import * as moment from 'moment';
import { DialogConfig, DialogOption, DialogButtonStyle } from '../../shared/ui/dialog/dialog-options';
import { PurchaseTypeEditorComponent } from '../purchase-type-editor/purchase-type-editor.component';
import { ReceiptTemplateEditorComponent } from '../receipt-template-editor/receipt-template-editor.component';
import { RegisterPrintEditorComponent } from '../register-print-editor/register-print-editor.component';
import { VendorLocationGridComponent } from '../vendor-location-grid/vendor-location-grid.component';
import { ReportChartComponent } from '../report-chart/report-chart.component';
import { ReportChartConfig, validCharts } from '../report-chart/report-chart-config';

// Components launchable from a Report template's headerActions (buttons in the
// report header, next to print). The action's `component` value selects the
// entry here; the dialog opens full-screen.
const REPORT_HEADER_COMPONENTS: { [key: string]: any } = {
  'vendor-location-grid': VendorLocationGridComponent,
};


@Component({
  selector: 'app-report-data',
  templateUrl: './report-data.component.html',
  styleUrls: ['./report-data.component.scss']
})
export class ReportDataComponent implements OnInit, OnDestroy {

  @ViewChild(FilterDateRangeComponent) dateRangePicker: FilterDateRangeComponent;
  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;

  reportReportKey$: Observable<any>;
  reportKey;

  activeReportConfig;
  dateRangeSettings: DateRangeSettings = new DateRangeSettings();

  tableWidth = 0;
  // Visible headerActions from the Report template (permission-filtered).
  headerActions: any[] = [];
  // True while a child route (detail panel / full-screen page) covers the grid.
  childRouteOpen = false;

  // Column resize (drag on a header cell's right edge). suppressHeaderClick
  // swallows the click that lands on the header cell right after a resize so
  // it doesn't toggle the sort.
  private resizingField = null;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private suppressHeaderClick = false;
  private resizeMoveListener: any = null;
  private resizeUpListener: any = null;
  template;
  availableFields;
  activeFields;
  totals;
  valueFilters;

  fieldAddOpen;
  fieldsSort: Subscription;

  canAdd = false;
  adding = false;
  canImport = false;

  listBuilderActive = false;
  reportFlexBasis = '100%';

  templateOptions;

  // Active print templates ({ name, key }) available for this report.
  printTemplates: any[] = [];
  // Chart configurations from the Report template (Report.charts) that can render
  // against its fields — drives the chart icon beside print.
  charts: ReportChartConfig[] = [];
  downloadTemplates: any[] = [];

  // ─── Variants ────────────────────────────────────────────
  variantsFeatureEnabled = false;
  variantsOpen = false;
  activeVariantKey: string | null = null;
  // scheduledReports.manage (general grant): shows "Send Scheduled Report…" in
  // the variant menu — the same gate as the + button on the Scheduled Reports report.
  canScheduleReports = false;
  variantNameInput = '';
  variantNameInputMode: 'new' | 'rename' | null = null;
  variantNameInputTargetKey: string | null = null;
  isMobile = false;

  reportSelected: Subscription;
  reportLoaded: Subscription;
  recordsUpdated: Subscription;
  loadErrorSub: Subscription;
  activeLocationSet: Subscription;
  permissionsLoaded: Subscription;

  valueFiltersUpdated: Subscription;
  routerEvents: Subscription;
  scrollEvents: Subscription;
  listBuilderSub: Subscription;
  variantQueryParamSub: Subscription;
  variantsChangedSub: Subscription;
  variantAppliedSub: Subscription;
  isMobileSub: Subscription;
  clientSettingsLoadedSub: Subscription;

  tsl = 0;

  records: any[];

  // A user-facing message shown in the results area when a report fails to load
  // (e.g. a report that requires a single location to be selected).
  loadMessage: string = null;

  activeDocumentId;

  modalRef;

  @ViewChild('importrecords') importrecordsEl: ElementRef<HTMLElement>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private reportService: ReportService,
    private authorizationService: AuthorizationService,
    private locationService: LocationService,
    private tagPrintService: TagPrintService,
    private tableScroll: ScrollDispatcher,
    private changeDetectorRef: ChangeDetectorRef,
    public dialog: MatDialog,
    private dragulaService: DragulaService,
    private ioService: IoService,
    private ngZone: NgZone,
    private listBuilderService: ListBuilderService,
    private clientSettingsService: ClientSettingsService,
    private responsiveService: ResponsiveService,
  ) {
    // The header row uses dragula group 'fields' for column reordering.
    // Create the group up front with a moves filter so grabbing a column's
    // resize handle never starts a column drag. direction: 'horizontal' makes
    // drop placement hit-test by X (the group previously used the vertical
    // default, which made left-to-right column drags unreliable).
    if (!this.dragulaService.find('fields')) {
      this.dragulaService.createGroup('fields', {
        direction: 'horizontal',
        moves: (el, container, handle) => !handle?.classList?.contains('resize-handle'),
      });
    }
  }

  ngOnInit() {

    this.reportSelected = this.reportService.reportSelectedObservable().subscribe(reportConfig => {
      LoadingComponent.showLoading();
      if (this.listBuilderActive) {
        this.listBuilderService.closeListBuilder();
      }
    //   this.activeReportConfig = reportConfig;
    });

    this.reportLoaded = this.reportService.reportLoadedObservable().subscribe(reportMeta => {
      console.log('REPORT LOADED');

      let updatedReportConfig: any = {
        key: reportMeta.template.key
      };

      updatedReportConfig.name = reportMeta.nameOverride || reportMeta.template.name;
      updatedReportConfig.description = reportMeta.template.description;

      updatedReportConfig.supportsIncrementalLoading = true;

      updatedReportConfig.viewRoute = reportMeta.template.viewRoute;
      updatedReportConfig.viewKey = reportMeta.template.viewKey;
      updatedReportConfig.viewIdPath = reportMeta.template.viewIdPath;
      updatedReportConfig.datePath = reportMeta.template.datePath;
      updatedReportConfig.locationPath = reportMeta.template.locationPath;

      updatedReportConfig.addPermission = reportMeta.template.addPermission;
      updatedReportConfig.addData = reportMeta.template.addData;
      updatedReportConfig.addRoute = reportMeta.template.addRoute;
      updatedReportConfig.addLink = reportMeta.template.addLink;
      updatedReportConfig.addDialog = reportMeta.template.addDialog;
      updatedReportConfig.dialogKey = reportMeta.template.dialogKey;
      updatedReportConfig.importKey = reportMeta.template.importKey;
      updatedReportConfig.listBuilder = reportMeta.template.listBuilder || false;

      this.activeReportConfig = updatedReportConfig;

      this.activeFields = reportMeta.activeFields;

      this.tableWidth = 0;
      if (this.activeFields && this.activeFields.length > 0) {
        for (const field of this.activeFields) {
          this.tableWidth += field.width;
        }
      }

      this.tableWidth += 50;

      this.template = reportMeta.template;
      this.updateHeaderActions();
      this.totals = reportMeta.totals;
      this.templateOptions = reportMeta.templateOptions;
      this.printTemplates = reportMeta.printTemplates || [];
      this.charts = validCharts(reportMeta.template.charts, reportMeta.template.fields);
      this.downloadTemplates = reportMeta.downloadTemplates || [];

      // Sync the date picker with the service's dateRange so the UI reflects the applied filter
      if (reportMeta.template.datePath) {
        this.applyReportDateConfig(reportMeta.template.dateConfig);
        const serviceRange = this.reportService.getDateRange();
        if (serviceRange?.startDate) {
          this.dateRangeSettings.dateRange = serviceRange;
        }
      }

      this.viewport.scrollToIndex(0);

      this.setAvailableFields();

      this.listBuilderService.setActiveFields(this.activeFields);
      this.listBuilderService.setActiveViewKey(reportMeta.template.viewKey || null);
      this.listBuilderService.setActiveViewRoute(reportMeta.template.viewRoute || null);
      this.listBuilderService.setActiveItemType(reportMeta.template.itemType || null);
      this.listBuilderService.setActiveListActions(reportMeta.template.listActions || []);

      this.checkReportAuthorizations();
    });

    this.recordsUpdated = this.reportService.recordsUpdatedObservable().subscribe( (newRecords) => {
      this.records = newRecords;
    });

    this.loadErrorSub = this.reportService.loadErrorObservable().subscribe( (message) => {
      this.loadMessage = message || null;
    });

    this.valueFilters = null;
    if (this.valueFiltersUpdated) {
      this.valueFiltersUpdated.unsubscribe();
    }
    this.valueFiltersUpdated = this.reportService.valueFiltersUpdatedObservable().subscribe(valueFilters => {
      this.valueFilters = valueFilters;
    });

    this.scrollEvents = this.tableScroll.scrolled().subscribe(x => {
      if (x) {
        const tableScrollLeft = x.getElementRef().nativeElement.scrollLeft;
        if (this.tsl != tableScrollLeft) {
          this.tsl = x.getElementRef().nativeElement.scrollLeft;
          this.changeDetectorRef.detectChanges();
        }
      }
    });

    this.activeLocationSet = this.locationService.activeLocationSetObservable().subscribe(location => {
      LoadingComponent.showLoading();
      this.reportService.refresh();
    });

    // On a fresh page load the permission map can arrive after the report does —
    // re-run the add/import checks so buttons don't stay hidden by the race.
    this.permissionsLoaded = this.authorizationService.permissionsLoadedObservable().subscribe(() => {
      this.checkReportAuthorizations();
    });

    this.fieldsSort = this.dragulaService.drop('fields').subscribe(({ name, el, target, source, sibling }) => {
      LoadingComponent.showLoading();
      this.reportService.setFieldsSequence(this.activeFields);
    });

    // Variants — feature flag (loaded post-auth) + responsive state
    this.variantsFeatureEnabled = this.clientSettingsService.isReportVariantsEnabled();
    this.clientSettingsLoadedSub = this.clientSettingsService.clientSettingsLoadedObservable().subscribe(() => {
      this.variantsFeatureEnabled = this.clientSettingsService.isReportVariantsEnabled();
    });
    this.isMobile = this.responsiveService.isMobile();
    this.isMobileSub = this.responsiveService.isMobile$.subscribe(m => this.isMobile = m);

    // Subscribe BEFORE setReportDocumentByPath so the initial variantApplied emission isn't lost.
    this.variantAppliedSub = this.reportService.variantAppliedObservable().subscribe(({ variantKey }) => {
      this.activeVariantKey = variantKey;
      this.changeDetectorRef.detectChanges();
    });

    this.variantsChangedSub = this.reportService.variantsChangedObservable().subscribe(() => {
      // Touch the reference so template re-evaluates getters cleanly
      this.changeDetectorRef.detectChanges();
    });

    // Listen to routes to get active documentId
    this.setReportDocumentByPath(this.router.url);
    this.childRouteOpen = this.route.children.length > 0;
    this.routerEvents = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.setReportDocumentByPath(event.url);
      // Column resize handles render only while the grid is the top view.
      // Child routes (details, permission matrices, bulk pages) cover the grid
      // with components that aren't all positioned — a positioned handle would
      // paint and hit-test through any non-positioned overlay.
      this.childRouteOpen = this.route.children.length > 0;
    });

    // Apply variant when only the ?variant= param changes (same report)
    this.variantQueryParamSub = this.route.queryParamMap.subscribe(qp => {
      if (!this.variantsFeatureEnabled) return;
      const variantParam = qp.get('variant');
      const activeReport = this.reportService.getActiveReportConfig();
      if (!activeReport || activeReport.key !== this.reportKey) return;

      const targetKey = (variantParam === 'system' || !variantParam) ? null : variantParam;
      if (this.reportService.getActiveVariantKey() !== targetKey) {
        this.reportService.applyVariantByKey(targetKey);
      }
    });

    this.listBuilderSub = this.listBuilderService.listBuilderActiveChangedObservable().subscribe(active => {
      this.listBuilderActive = active;
      this.reportFlexBasis = active ? '50%' : '100%';
    });

    // this.reportService.loadReport(0);
  }

  ngOnDestroy() {
    this.removeResizeListeners();
    if (this.permissionsLoaded) this.permissionsLoaded.unsubscribe();
    this.activeLocationSet.unsubscribe();
    this.fieldsSort.unsubscribe();
    this.valueFiltersUpdated.unsubscribe();
    this.recordsUpdated.unsubscribe();
    if (this.loadErrorSub) this.loadErrorSub.unsubscribe();
    this.reportLoaded.unsubscribe();
    this.reportSelected.unsubscribe();
    this.routerEvents.unsubscribe();
    this.scrollEvents.unsubscribe();
    if (this.listBuilderSub) this.listBuilderSub.unsubscribe();
    if (this.variantQueryParamSub) this.variantQueryParamSub.unsubscribe();
    if (this.variantsChangedSub) this.variantsChangedSub.unsubscribe();
    if (this.variantAppliedSub) this.variantAppliedSub.unsubscribe();
    if (this.isMobileSub) this.isMobileSub.unsubscribe();
    if (this.clientSettingsLoadedSub) this.clientSettingsLoadedSub.unsubscribe();
    // Unsubscribe from dragularService
    // this.dragulaService.destroy('fields');

  }

  dateRangeUpdated( range ) {
    console.log('reportDataComponent dateRangeUpdated: ', range);
    LoadingComponent.showLoading();
    this.reportService.setDateRange(range);
  }

  // A report may carry a `dateConfig` for the header picker (Report document;
  // same keys as DatePickerConfig). Honored here: `includeTime` (false hides
  // the hour/minute selects and makes every range whole days — Daily Sales
  // Totals, Daily Balance Sheet, X-Out are keyed by business day, so a time of
  // day never meant anything on them) and `label` (caption under the box).
  // The picker is re-created when either changes (new settings object), and a
  // date-only report snaps a carried-over range to whole days so a window like
  // "10:00 AM - 2:00 PM" from another report cannot silently hide its rows.
  applyReportDateConfig(dateConfig: any) {
    const includeTime = !(dateConfig && dateConfig.includeTime === false);
    const label = (dateConfig && dateConfig.label) || null;
    const current = this.dateRangeSettings.dateConfig;
    if (includeTime !== current.includeTime || label !== current.label) {
      const settings = new DateRangeSettings();
      settings.dateConfig.includeTime = includeTime;
      settings.dateConfig.label = label;
      settings.dateRange = this.dateRangeSettings.dateRange;
      this.dateRangeSettings = settings;
    }
    if (!includeTime) {
      const range = this.reportService.getDateRange();
      if (range?.startDate && range?.endDate) {
        const start = moment(range.startDate).startOf('day');
        const end = moment(range.endDate).endOf('day');
        if (!start.isSame(range.startDate) || !end.isSame(range.endDate)) {
          this.reportService.setDateRange(new DateRange(start, end));
        }
      }
    }
  }



  async setReportDocumentByPath(path) {
    // Strip query string for path-part parsing; we read variant from queryParamMap separately.
    const pathOnly = (path || '').split('?')[0];
    const pathParts = pathOnly.split('/');
    const variantParam = this.route.snapshot.queryParamMap.get('variant');
    console.log('pathParts: ', pathParts);

    if (pathParts[2]) {
      const nextReportKey = pathParts[2];
      if (nextReportKey != this.reportKey) {
        this.reportKey = nextReportKey;

        // Wait for both client settings (so the variants feature flag is accurate
        // — it loads async after auth and may be false on a fresh page load) and
        // reports metadata (so getDefaultVariantKey returns the cached default).
        await Promise.all([
          this.clientSettingsService.loadClientSettings(),
          this.reportService.init(),
        ]);
        this.variantsFeatureEnabled = this.clientSettingsService.isReportVariantsEnabled();

        // A concurrent navigation may have overridden reportKey while awaiting; bail if so.
        if (this.reportKey !== nextReportKey) return;

        // Resolve which variant to apply on first load.
        //   'system'   → explicit system default
        //   <key>      → that specific variant
        //   undefined  → auto: user's default if set, else system
        let variantKeyToApply: string | null | undefined;
        if (!this.variantsFeatureEnabled) {
          variantKeyToApply = undefined;
        } else if (variantParam === 'system') {
          variantKeyToApply = 'system';
        } else if (variantParam) {
          variantKeyToApply = variantParam;
        } else {
          const userDefault = this.reportService.getDefaultVariantKey(nextReportKey);
          if (userDefault) {
            // Reflect the applied variant in the URL so reloads / shares are consistent.
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { variant: userDefault },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
            variantKeyToApply = userDefault;
          } else {
            variantKeyToApply = undefined;
          }
        }

        this.reportService.setActiveReport(
          { key: nextReportKey },
          [],
          variantKeyToApply,
        );
      }
    }

    if (pathParts.length == 5) {
      this.activeDocumentId = pathParts[4];
    } else if (pathParts.length == 6) {
      this.activeDocumentId = pathParts[5];
    } else if (pathParts.length == 7) {
      // Full-screen record sub-page, e.g. details/:viewKey/:documentId/vendors —
      // keep the record's row highlighted behind it.
      this.activeDocumentId = pathParts[5];
    } else {
      this.activeDocumentId = null;
    }
  }

  filterLink(link) {
    this.router.navigate(
      ['reports', this.activeReportConfig.key, this.activeReportConfig.itemType, link],
      { queryParamsHandling: 'preserve' },
    );
  }

  // Header action buttons from the Report template (headerActions), filtered
  // by their optional permission key. Recomputed when the report loads and
  // again when the permission map arrives.
  updateHeaderActions() {
    this.headerActions = (this.template?.headerActions || []).filter(action =>
      !action.permission || this.authorizationService.checkPermission(action.permission, 'any'));
  }

  launchHeaderAction(action) {
    const componentClass = REPORT_HEADER_COMPONENTS[action.component];
    if (!componentClass) {
      AppComponent.showSnack('Component "' + action.component + '" is not registered.', 'OK', 4000);
      return;
    }
    AppComponent.dialog.open(componentClass, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      panelClass: 'opaque-dialog',
      data: {
        reportKey: this.reportKey,
        action,
      },
    });
  }

  checkReportAuthorizations() {
    this.updateHeaderActions();
    if (this.activeReportConfig) {

      this.canAdd = false;

      if (this.activeFields && this.activeFields.length > 0) {
        for (let field of this.activeFields) {
          if (field.instantEditPermission) {
            field.canEdit = this.authorizationService.checkPermission(field.instantEditPermission, 'any', false);
          }
        }
      }

      if (this.activeReportConfig.addRoute || this.activeReportConfig.addLink || (this.activeReportConfig.addDialog && this.activeReportConfig.dialogKey)) {
        if (this.activeReportConfig.addPermission) {
          if (this.activeReportConfig.locationPath && this.activeReportConfig.locationPath != 'any' && this.activeReportConfig.locationPath != '_id') {
            // This permission is location specific
            if (this.locationService.getActiveLocation()._id) {
              // A single location is selected
              if (this.authorizationService.checkPermission(this.activeReportConfig.addPermission, this.locationService.getActiveLocation()._id)) {
                this.canAdd = true;
              }
            } else {
              // No location is selected
              for (const location of this.locationService.getLocations()) {
                if (location._id) {
                  if (this.authorizationService.checkPermission(this.activeReportConfig.addPermission, location._id, false)) {
                    this.canAdd = true;
                  }
                }
              }
            }

          } else {
            // This permission is NOT location specific
            if (this.authorizationService.checkPermission(this.activeReportConfig.addPermission, 'general', false)) {
              this.canAdd = true;
            }

            if (this.authorizationService.checkPermission(this.activeReportConfig.addPermission)) {
              this.canAdd = true;
            }
          }
        } else {
          // No permission required — allow add
          this.canAdd = true;
        }
      }
    }


    if (this.activeReportConfig && this.activeReportConfig.importKey) {
      this.canImport = this.authorizationService.checkPermission(
        'view.' + this.activeReportConfig.importKey + '.import', 'any', false
      );
    } else if (this.activeReportConfig && this.activeReportConfig.importRoute) {
      this.canImport = this.authorizationService.checkPermission(this.activeReportConfig.importPermission || 'Developer', 'general', false);
    }
    // "Send Scheduled Report…" in the variant menu: tenant-wide grant, same gate
    // as the Scheduled Reports report's + button.
    this.canScheduleReports = this.authorizationService.checkPermission('scheduledReports.manage', 'general', false);

  }

  getDocumentConfig() {
    let documentConfig: any = {};

    for (const configParam of [
      'itemType',
      'addPermission',
      'addRoute',
      'addData',
      'viewRoute',
      'viewKey',
      'viewIdPath',
      'addLink',
      'addDialog',
      'dialogKey'
    ]) {
      if (this.activeReportConfig[configParam] !== undefined) {
        documentConfig[configParam] = this.activeReportConfig[configParam];
      }
    }

    return documentConfig;
  }

  add() {
    const documentConfig = this.getDocumentConfig();

    if (documentConfig.addDialog && documentConfig.dialogKey) {
      this.openEditorDialog(documentConfig.dialogKey, null);
      return;
    }

    if (documentConfig.addLink) {
      this.router.navigate([documentConfig.addLink], { queryParamsHandling: 'preserve' });
      return;
    }

    this.reportService.setAdding(true);

    let addData = {
      locationId: null
    };
    if (documentConfig.addData) {
      addData = documentConfig.addData;
    }
    addData.locationId = this.locationService.getActiveLocation()._id;

    this.ioService.post(documentConfig.addRoute, addData).then((addResponse: any) => {
      this.setDetailRecord(addResponse);
      this.reportService.loadReport(0);
    });

  }

  // Reports can open records in a full-screen editor dialog instead of routing —
  // dialogKey names the editor; the registry maps it to a component.
  openEditorDialog(dialogKey: string, recordId: string | null) {
    switch (dialogKey) {
      case 'purchase-type-editor':
        PurchaseTypeEditorComponent.showModal(recordId).afterClosed().subscribe(result => {
          if (result && result.saved) {
            this.reportService.refresh();
          }
        });
        break;

      case 'receipt-template-editor':
        ReceiptTemplateEditorComponent.showModal(recordId).afterClosed().subscribe(result => {
          if (result && result.saved) {
            this.reportService.refresh();
          }
        });
        break;

      case 'register-print-editor':
        RegisterPrintEditorComponent.showModal(recordId).afterClosed().subscribe(result => {
          if (result && result.saved) {
            this.reportService.refresh();
          }
        });
        break;

      default:
        console.log('Unknown report dialogKey: ', dialogKey);
    }
  }

  setDetailRecord(record: any) {
    const documentConfig = this.getDocumentConfig();
    // viewIdPath lets a report open its detail View by a non-_id record field
    // (e.g. an audit row opens the changed document, not the audit record).
    const recordId = (documentConfig.viewIdPath && record[documentConfig.viewIdPath]) || record._id;
    if (documentConfig.dialogKey) {
      this.openEditorDialog(documentConfig.dialogKey, recordId);
      return;
    }
    if (documentConfig.viewKey) {
      if (!this.authorizationService.checkPermission('view.' + documentConfig.viewKey + '.view', 'any', false)) {
        return;
      }
      this.router.navigate(
        ['reports', this.template.key, 'details', documentConfig.viewKey, recordId],
        { queryParamsHandling: 'preserve' },
      );
      this.activeDocumentId = recordId;
    } else if (documentConfig.viewRoute) {
      this.router.navigate([documentConfig.viewRoute, recordId], { queryParamsHandling: 'preserve' });
      this.activeDocumentId = recordId;
    } else if (this.template.itemType) {
      this.router.navigate(
        ['reports', this.template.key, this.template.itemType, recordId],
        { queryParamsHandling: 'preserve' },
      );
    }
  }

  setAvailableFields() {
    this.availableFields = [];
    for (const potentialField of this.template.fields) {
      if (this.activeFields.indexOf(potentialField) == -1) {
        this.availableFields.push(potentialField);
      }
    }
  }

  toggleFieldAdd() {
    this.fieldAddOpen = !this.fieldAddOpen;
  }

  addField(field) {
    LoadingComponent.showLoading();
    this.reportService.addField(field);
  }

  removeField(event: Event, field) {
    event.stopPropagation();

    LoadingComponent.showLoading();

    const activeIndex = this.activeFields.indexOf(field);
    if (activeIndex > -1) {
      this.activeFields.splice(activeIndex, 1);
    }
    this.reportService.removeField(field);
  }

  /**
   * Triggered by the download button. Reports without download templates download
   * the standard CSV immediately, exactly as before. With templates, prompts the
   * user to choose a format — CSV remains the default first option.
   */
  downloadReport() {
    if (!this.downloadTemplates || this.downloadTemplates.length === 0) {
      this.reportService.download();
      return;
    }

    const dialogConfig = new DialogConfig();
    dialogConfig.title = 'Download';
    dialogConfig.message = 'Choose a format:';
    dialogConfig.options = [new DialogOption('CSV (default)', 'csv-default', DialogButtonStyle.Normal)].concat(
      this.downloadTemplates.map((t) => new DialogOption(t.name, t.key, DialogButtonStyle.Normal))
    );

    AppComponent.showDialog(dialogConfig).afterClosed().subscribe((result) => {
      if (!result || !result.value) {
        return;
      }

      if (result.value === 'csv-default') {
        this.reportService.download();
        return;
      }

      const downloadTemplate = this.downloadTemplates.find((t) => t.key === result.value);
      if (downloadTemplate) {
        const downloadPromise = this.reportService.download(downloadTemplate.key, downloadTemplate.extension || 'csv', downloadTemplate.fileName || null);
        if (downloadPromise) {
          downloadPromise.catch((err) => {
            console.log('Download error: ', err);
            AppComponent.showSnack('Unable to generate the download.', 'Dismiss', 5000);
          });
        }
      }
    });
  }

  printReport(printTemplate) {
    this.reportService.printReport(printTemplate.key);
  }

  setSearch(event: any) {
    LoadingComponent.showLoading();
    this.reportService.setSearch(event.target.value);
  }

  setSort(field) {
    if (this.suppressHeaderClick) {
      return;
    }
    LoadingComponent.showLoading();
    this.reportService.setSort(field);
  }

  // ==== Column resize ================================================

  startColumnResize(event: MouseEvent, field) {
    if (this.isMobile) {
      return;
    }
    // preventDefault stops a text selection from starting alongside the drag.
    event.preventDefault();
    event.stopPropagation();

    this.resizingField = field;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = Number(field.width) || 100;

    this.resizeMoveListener = (e: MouseEvent) => this.onColumnResizeMove(e);
    this.resizeUpListener = () => this.endColumnResize();
    document.addEventListener('mousemove', this.resizeMoveListener);
    document.addEventListener('mouseup', this.resizeUpListener);
  }

  private onColumnResizeMove(event: MouseEvent) {
    if (!this.resizingField) {
      return;
    }
    const width = Math.max(40, Math.round(this.resizeStartWidth + event.clientX - this.resizeStartX));
    if (width !== this.resizingField.width) {
      this.resizingField.width = width;
      this.updateTableWidth();
    }
  }

  private endColumnResize() {
    this.removeResizeListeners();
    const changed = this.resizingField && this.resizingField.width !== this.resizeStartWidth;
    this.resizingField = null;
    if (changed) {
      this.persistColumnWidths();
      // The mouseup also completes a click on the header cell — swallow it so
      // the resize doesn't toggle the sort.
      this.suppressHeaderClick = true;
      setTimeout(() => this.suppressHeaderClick = false);
    }
  }

  private removeResizeListeners() {
    if (this.resizeMoveListener) {
      document.removeEventListener('mousemove', this.resizeMoveListener);
      this.resizeMoveListener = null;
    }
    if (this.resizeUpListener) {
      document.removeEventListener('mouseup', this.resizeUpListener);
      this.resizeUpListener = null;
    }
  }

  private updateTableWidth() {
    let tableWidth = 0;
    if (this.activeFields && this.activeFields.length > 0) {
      for (const field of this.activeFields) {
        tableWidth += field.width;
      }
    }
    this.tableWidth = tableWidth + 50;
  }

  // Overrides live on the template's field objects (activeFields share those
  // references): any field whose width differs from its report default. The
  // full set is saved each time so overrides on currently-hidden fields survive.
  private columnWidthOverrides() {
    return (this.template?.fields || [])
      .filter(field => field.defaultWidth != null && field.width !== field.defaultWidth)
      .map(field => ({ path: field.path, width: field.width }));
  }

  hasColumnWidthOverrides() {
    return this.columnWidthOverrides().length > 0;
  }

  private persistColumnWidths() {
    if (!this.reportKey) {
      return;
    }
    this.reportService.saveColumnWidths(this.reportKey, this.columnWidthOverrides())
      .catch((e) => console.log('saveColumnWidths error: ', e));
  }

  resetColumnWidth(event: Event, field) {
    event.stopPropagation();
    event.preventDefault();
    if (field.defaultWidth != null && field.width !== field.defaultWidth) {
      field.width = field.defaultWidth;
      this.updateTableWidth();
      this.persistColumnWidths();
    }
  }

  resetAllColumnWidths(event: Event) {
    event.stopPropagation();
    let changed = false;
    for (const field of (this.template?.fields || [])) {
      if (field.defaultWidth != null && field.width !== field.defaultWidth) {
        field.width = field.defaultWidth;
        changed = true;
      }
    }
    if (changed) {
      this.updateTableWidth();
      this.persistColumnWidths();
    }
    this.fieldAddOpen = false;
  }

  reportScrolled(topRecordIndex) {

    this.reportService.reportScrolled(topRecordIndex);

    // console.log('Report Scrolled: ', topRecordIndex);


    // Don't bother loading chunks on scroll for custom reports
    // if (this.activeReportConfig.chunkSize) {
    //   console.log('load chunks of a designated size');

    //   this.reportService.loadSpecifiedChunk(topRecordIndex, this.activeReportConfig.chunkSize);
    // } else {

      // console.log('topRecordIndex: ', topRecordIndex);
      // console.log('activeReportConfig: ', this.activeReportConfig);

      // if (
        // !this.activeReportConfig || 
        // !this.activeReportConfig.type || 
        // this.activeReportConfig.type != 'custom' || 
        // this.activeReportConfig?.supportsIncrementalLoading
      // ) {
        // this.reportService.loadChunk(topRecordIndex);
      // }
    // }
  }

  trackByIndex(index: number) {
    return index;
  }

  getValueByPath(o, path) {
    if (o == null) {
      return;
    } else {
      path = path.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
      path = path.replace(/^\./, '');           // strip a leading dot
      const a = path.split('.');
      for (let i = 0, n = a.length; i < n; ++i) {
          let k = a[i];
          if (k in o) {
              o = o[k];
          } else {
              return;
          }
      }
      return o;
    }
  }

  refreshReport() {
    LoadingComponent.showLoading();
    this.reportService.refresh();
  }

  updateReport = async () => {
    const updateResult = await this.ioService.post('/report/updateFromRemote', {
      reportId: this.activeReportConfig._id
    });
  }

  onRightClick(record, field) {
    console.log('right click');
    console.log('record: ', record);
    console.log('field: ', field);

    // Numeric columns get a small range panel (equals / at least / at most /
    // between) instead of a bare equality chip — "balance over $20" is the
    // usual question, not "balance exactly $20.00".
    if (this.isRangeFilterType(field.type)) {
      this.openRangeFilter(field, this.getValueByPath(record, field.path));
      return false;
    }

    if ([
      'string',
      'id',
      'remoteId',
      'boolean',
      'sourceCollection',
    ].indexOf(field.type) != -1) {

      if (field.type === 'sourceCollection') {
        this.reportService.filterByFieldValue(field, record['_scId_' + field.path],
          this.getValueByPath(record, field.path));
      } else {
        this.reportService.filterByFieldValue(field, this.getValueByPath(record, field.path));
      }
    } else {
      console.log('No value filter configured for type: ' + field.type);
    }

    return false;
  }

  preventPropagation(event) {
    event.stopPropagation();
  }

  // ==== Range filter (currency / number columns) ====
  rangeFilter: { field: any, mode: string, value: string, min: string, max: string, currency: boolean } = null;

  isRangeFilterType(type: string): boolean {
    return ['currency', 'currencyInverse', 'currencyDetail', 'number', 'numberInverse'].indexOf(type) !== -1;
  }

  openRangeFilter(field, rawValue) {
    const currency = field.type !== 'number' && field.type !== 'numberInverse';
    const numeric = Number(rawValue);
    const display = isNaN(numeric) ? '' : (currency ? (numeric / 100).toFixed(2) : String(numeric));
    this.rangeFilter = { field, mode: 'equals', value: display, min: display, max: display, currency };
  }

  cancelRangeFilter() {
    this.rangeFilter = null;
  }

  applyRangeFilter() {
    const rf = this.rangeFilter;
    if (!rf) {
      return;
    }
    const parse = (text: string) => {
      const n = parseFloat(String(text).replace(/[$,\s]/g, ''));
      return isNaN(n) ? null : n;
    };
    const fmt = (n: number) => rf.currency ? '$' + n.toFixed(2) : String(n);
    let value: any;
    let displayValue: string;
    if (rf.mode === 'equals') {
      const n = parse(rf.value);
      if (n === null) { return; }
      value = rf.currency ? Math.round(n * 100) : n;
      displayValue = fmt(n);
    } else {
      const min = rf.mode === 'atMost' ? null : parse(rf.min);
      const max = rf.mode === 'atLeast' ? null : parse(rf.max);
      if (min === null && max === null) { return; }
      value = {};
      if (min !== null) { value.min = min; }
      if (max !== null) { value.max = max; }
      displayValue = min !== null && max !== null ? fmt(min) + ' – ' + fmt(max) : (min !== null ? '≥ ' + fmt(min) : '≤ ' + fmt(max));
    }
    this.reportService.filterByFieldValue(rf.field, value, displayValue);
    this.rangeFilter = null;
  }

  setQuickEditFocus() {
      // Set focus on the input
      setTimeout(() => {
        const inputEl: HTMLInputElement = document.getElementById('active-quick-edit') as HTMLInputElement;
        if (inputEl) {
          inputEl.focus();
          // Select the text
          inputEl.select();
        }
      }, 100);
  }

  fieldClick(event, record, field) {
    // console.log('field click');
    // console.log('record: ', record);
    // console.log('field: ', field);

    // Prevent event bubbliing
    if (field.instantEditSavePath && !this.activeDocumentId) {
      event.stopPropagation();
      record.initialValue = this.getValueByPath(record, field.path);

      switch (field.type) {
        case 'currency':
          record[field.path] = (record[field.path]/100).toFixed(2);
          break;

        //  case 'number':
        //   if (record[field.path] == null) {
        //     record[field.path] = 0;
        //   }
        //   record[field.path] = parseFloat(record[field.path]);
          break;
      }

      record.editingField = field.path;

      this.setQuickEditFocus();
    }
  }

  blurField(event, record, field) {
    this.cancelFieldEdit(record, field);
  }

  updateField(event, record, activeField) {
    // console.log('update field');
    // console.log('event: ', event);

    // See if this is tab
    if (event.keyCode == 9) {
      console.log('tab pressed');
      event.preventDefault();
      this.saveField(record, activeField);
      
      // Set focus on this field on the next record
      const recordIndex = this.records.indexOf(record);
      if (recordIndex < this.records.length - 1) {
        const nextRecord = this.records[recordIndex + 1];
        this.fieldClick(event, nextRecord, activeField);
      }
    }

    // See if this is enter
    if (event.keyCode == 13) {
      console.log('enter pressed');
      this.saveField(record, activeField);
    }

    // See if this is escape
    if (event.keyCode == 27) {
      console.log('escape pressed');
      this.cancelFieldEdit(record, activeField);
    }
  }

  cancelFieldEdit(record, activeField) {
    delete record.editingField;
    record[activeField.path] = record.initialValue;
  }

  saveField(record, field) {
    // console.log('save field');
    // console.log('record: ', record);
    this.reportService.saveField(record, field);
  }

  removeValueFilterIndex(valueFilterIndex) {
    console.log('valueFilterIndex: ', valueFilterIndex);
    this.reportService.removeValueFilter(valueFilterIndex);
  }

  onImportFileChange(event) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];

      if (this.activeReportConfig.importKey) {
        // New View-based import flow
        LoadingComponent.showLoading();
        this.ioService.upload('view/importFromView', {
          viewKey: this.activeReportConfig.importKey,
          locationId: this.locationService.getActiveLocation()._id
        }, file).subscribe(uploadEvent => {
          const response: any = uploadEvent;
          if (response.body && response.body.importId) {
            LoadingComponent.hideLoading();
            const result = response.body;
            AppComponent.showSnack(
              `Import complete: ${result.added} added, ${result.updated} updated` +
              (result.errors?.length > 0 ? `, ${result.errors.length} errors` : ''),
              'OK', 5000
            );
            if (result.errors?.length > 0) {
              console.log('Import errors:', result.errors);
            }
            this.reportService.refresh();
          }
        });
      } else if (this.activeReportConfig.importRoute) {
        // Legacy import flow
        this.ioService.upload(this.activeReportConfig.importRoute, {
          locationId: this.locationService.getActiveLocation()._id
        }, file).subscribe(uploadEvent => {
          console.log(uploadEvent);
        });
      }

      // Reset the input so the same file can be re-selected
      event.target.value = '';
    }
  }

  importRecords() {
    const el: HTMLElement = this.importrecordsEl.nativeElement;
    el.click();
  }

  // ─── Print ─────────────────────────────────────────────────

  // Server-side fallback template that renders the current view (active fields,
  // filters, sort, search) for any report — no per-report template required.
  private static readonly GENERIC_PRINT_KEY = 'generic-print';

  // Above this row count, printing fetches enough data to warrant a confirmation.
  private static readonly PRINT_CONFIRM_ROWS = 5000;

  /**
   * Triggered by the print button or Cmd/Ctrl+P. Every report can print its
   * current view via the generic server template; reports with their own print
   * templates get a chooser with "Current View" as the first option. The output
   * is rendered in a new window using the currently displayed report settings.
   */
  print() {
    if (!this.activeReportConfig) {
      return;
    }

    // The popup is opened at the LAST user interaction of the flow, never
    // before a dialog — an already-open popup steals focus and hides the
    // prompt. Every dialog button click carries its own user activation, so
    // the popup blocker allows window.open from the close handlers too.
    const count = this.totals?.count || 0;
    if (count > ReportDataComponent.PRINT_CONFIRM_ROWS) {
      const confirmConfig = new DialogConfig();
      confirmConfig.title = 'Print';
      confirmConfig.message = `This will print all ${count.toLocaleString()} rows. Continue?`;
      confirmConfig.options = [
        new DialogOption('Print', 'print', DialogButtonStyle.Confirm),
        new DialogOption('Cancel', 'cancel', DialogButtonStyle.Cancel),
      ];

      AppComponent.showDialog(confirmConfig).afterClosed().subscribe((result) => {
        if (result && result.value === 'print') {
          this.choosePrintTemplate();
        }
      });
      return;
    }

    this.choosePrintTemplate();
  }

  /**
   * Chart icon: one configured chart opens directly; several offer a chooser.
   * Charts load every row in the current range, so large reports confirm first
   * (same threshold as print).
   */
  openChart() {
    if (!this.activeReportConfig || this.charts.length === 0) {
      return;
    }

    const count = this.totals?.count || 0;
    if (count > ReportDataComponent.PRINT_CONFIRM_ROWS) {
      const confirmConfig = new DialogConfig();
      confirmConfig.title = 'Chart';
      confirmConfig.message = `This will chart all ${count.toLocaleString()} rows. Continue?`;
      confirmConfig.options = [
        new DialogOption('Chart', 'chart', DialogButtonStyle.Confirm),
        new DialogOption('Cancel', 'cancel', DialogButtonStyle.Cancel),
      ];
      AppComponent.showDialog(confirmConfig).afterClosed().subscribe((result) => {
        if (result && result.value === 'chart') {
          this.chooseChart();
        }
      });
      return;
    }

    this.chooseChart();
  }

  private chooseChart() {
    if (this.charts.length === 1) {
      this.showChart(this.charts[0]);
      return;
    }

    const dialogConfig = new DialogConfig();
    dialogConfig.title = 'Charts';
    dialogConfig.message = 'Choose a chart:';
    dialogConfig.stackOptions = true;
    dialogConfig.options = this.charts.map((chart) => new DialogOption(chart.name, chart.key, DialogButtonStyle.Normal));

    AppComponent.showDialog(dialogConfig).afterClosed().subscribe((result) => {
      const chart = result && result.value ? this.charts.find((c) => c.key === result.value) : null;
      if (chart) {
        this.showChart(chart);
      }
    });
  }

  private showChart(chart: ReportChartConfig) {
    ReportChartComponent.showModal(chart, this.activeReportConfig.name, this.template?.fields || []);
  }

  private openPrintWindow(): Window | null {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write('<p style="font-family: sans-serif; padding: 24px;">Preparing print&hellip;</p>');
    }
    return printWindow;
  }

  private choosePrintTemplate() {
    if (!this.printTemplates || this.printTemplates.length === 0) {
      this.renderPrint(ReportDataComponent.GENERIC_PRINT_KEY, this.openPrintWindow());
      return;
    }

    const dialogConfig = new DialogConfig();
    dialogConfig.title = 'Print';
    dialogConfig.dismissOnBackdrop = true;
    dialogConfig.options = [
      new DialogOption('Current View', ReportDataComponent.GENERIC_PRINT_KEY, DialogButtonStyle.Normal),
    ].concat(this.printTemplates.map(
      (t) => new DialogOption(t.name, t.key, DialogButtonStyle.Normal)
    ));

    AppComponent.showDialog(dialogConfig).afterClosed().subscribe((result) => {
      if (!result || !result.value) {
        return;
      }
      if (result.value === ReportDataComponent.GENERIC_PRINT_KEY) {
        this.renderPrint(result.value, this.openPrintWindow());
        return;
      }
      // A print template (tags/labels): silently to the store's tag printer when
      // an ISE desktop app is online there, else the popup + print dialog.
      const input = this.reportService.getLoadRecordsInput();
      delete input.clientConfig.skip;
      input.templateKey = result.value;
      input.clientConfig.download = true;
      // Location-aware tag handlers (store price) read a top-level locationId.
      try {
        input.locationId = this.locationService.getActiveLocationId() || null;
      } catch (e) {
        input.locationId = null;
      }
      this.tagPrintService.print('report/loadRecords', input, {
        description: this.activeReportConfig ? this.activeReportConfig.name : undefined,
      }).catch((err) => {
        console.log('Print render error: ', err);
        AppComponent.showSnack(err?.error?.msg || 'Unable to generate print output.', 'Dismiss', 5000);
      });
    });
  }

  /**
   * Cmd/Ctrl+P prints the report through the print flow instead of the browser's
   * native print, which can't see the virtually scrolled rows. Only intercepts on
   * the report list itself — with a detail record open, native print is untouched.
   * (Printing from the browser's File menu can't be intercepted.)
   */
  @HostListener('document:keydown', ['$event'])
  onKeydownPrint(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey
      && (event.key === 'p' || event.key === 'P')) {
      if (!this.activeReportConfig || this.activeDocumentId) {
        return;
      }
      event.preventDefault();
      this.print();
    }
  }

  private renderPrint(templateKey: string, printWindow: Window | null) {
    this.reportService.getPrintRender(templateKey).then((rendered: string) => {
      if (!printWindow) {
        return;
      }
      printWindow.document.open();
      printWindow.document.write(rendered || '');
      printWindow.document.close();
      printWindow.focus();
    }).catch((err) => {
      console.log('Print render error: ', err);
      if (printWindow) {
        printWindow.close();
      }
      const message = err?.error?.msg || 'Unable to generate print output.';
      AppComponent.showSnack(message, 'Dismiss', 5000);
    });
  }

  // ─── Variants ──────────────────────────────────────────────

  @HostListener('document:mousedown', ['$event'])
  onDocumentMousedownForVariants(event: MouseEvent) {
    if (!this.variantsOpen) return;
    const target = event.target as HTMLElement;
    if (!target.closest('.variants-control')) {
      this.closeVariantsMenu();
    }
  }

  get variantsControlVisible(): boolean {
    return this.variantsFeatureEnabled && !this.isMobile && !!this.reportKey;
  }

  get variants(): any[] {
    return this.reportKey ? this.reportService.getVariants(this.reportKey) : [];
  }

  get defaultVariantKey(): string | null {
    return this.reportKey ? this.reportService.getDefaultVariantKey(this.reportKey) : null;
  }

  get defaultVariant(): any | null {
    const key = this.defaultVariantKey;
    if (!key) return null;
    return this.variants.find(v => v.key === key) || null;
  }

  // Create a schedule for the current report + variant (columns/sorts snapshot,
  // current location selection) and open its settings panel.
  async sendScheduledReport() {
    this.variantsOpen = false;
    const report = this.activeReportConfig;
    if (!report) return;
    const variant = this.activeVariantKey ? this.variants.find(v => v.key === this.activeVariantKey) : null;
    const activeLocation = this.locationService.getActiveLocation();
    try {
      const created: any = await this.ioService.post('/scheduledReport/create', {
        reportKey: report.key,
        variantKey: variant ? variant.key : null,
        variantName: variant ? variant.name : 'System Default',
        activeFields: variant ? variant.activeFields : (report.clientConfig?.activeFields || null),
        sorts: variant ? variant.sorts : (this.reportService.getSorts() || null),
        locationIds: activeLocation && activeLocation._id ? [activeLocation._id] : [],
      });
      if (created && created._id) {
        this.router.navigate(['reports', 'scheduled-reports', 'details', 'scheduled-report', created._id]);
      }
    } catch (e) {
      AppComponent.showSnack(e?.error?.msg || 'Could not create the scheduled report.', 'OK', 5000);
    }
  }

  get activeVariantName(): string {
    if (!this.activeVariantKey) return 'System Default';
    const v = this.variants.find(x => x.key === this.activeVariantKey);
    return v ? v.name : 'System Default';
  }

  toggleVariantsMenu() {
    this.variantsOpen = !this.variantsOpen;
    if (!this.variantsOpen) {
      this.variantNameInputMode = null;
      this.variantNameInput = '';
      this.variantNameInputTargetKey = null;
    }
  }

  closeVariantsMenu() {
    this.variantsOpen = false;
    this.variantNameInputMode = null;
    this.variantNameInput = '';
    this.variantNameInputTargetKey = null;
  }

  // Navigate to a variant. 'system' explicitly forces System Default (overriding the user's
  // default-fallback in the queryParamMap subscription); otherwise sets ?variant=key.
  selectVariant(variantKey: string | null | 'system') {
    this.closeVariantsMenu();
    const variant = (!variantKey || variantKey === 'system') ? 'system' : variantKey;
    const targetActiveKey = variant === 'system' ? null : variant;

    // If the URL already has this variant, router.navigate is a no-op and
    // queryParamMap won't refire — apply directly so a stale service state
    // (e.g. system-default applied during a feature-flag race on refresh)
    // still catches up to what the user clicked.
    if (this.reportService.getActiveVariantKey() !== targetActiveKey) {
      this.reportService.applyVariantByKey(targetActiveKey);
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { variant },
      queryParamsHandling: 'merge',
    });
  }

  startSaveAsNew() {
    this.variantNameInputMode = 'new';
    this.variantNameInput = '';
    this.variantNameInputTargetKey = null;
    setTimeout(() => {
      const el = document.getElementById('variant-name-input') as HTMLInputElement;
      if (el) el.focus();
    }, 0);
  }

  async confirmSaveAsNew() {
    const name = (this.variantNameInput || '').trim();
    if (!name) return;
    try {
      const saved = await this.reportService.saveCurrentAsVariant(name);
      this.closeVariantsMenu();
      if (saved?.key) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { variant: saved.key },
          queryParamsHandling: 'merge',
        });
      }
    } catch (e: any) {
      AppComponent.showSnack(e?.error?.msg || e?.msg || 'Failed to save variant.', 'OK', 4000);
    }
  }

  async updateActiveVariant() {
    if (!this.activeVariantKey) return;
    this.closeVariantsMenu();
    try {
      await this.reportService.updateVariantWithCurrentState(this.activeVariantKey);
      AppComponent.showSnack('Variant saved.', 'OK', 3000);
    } catch (e: any) {
      AppComponent.showSnack(e?.error?.msg || e?.msg || 'Failed to save variant.', 'OK', 4000);
    }
  }

  startRenameActive() {
    if (!this.activeVariantKey) return;
    const current = this.variants.find(v => v.key === this.activeVariantKey);
    this.variantNameInputMode = 'rename';
    this.variantNameInput = current?.name || '';
    this.variantNameInputTargetKey = this.activeVariantKey;
    setTimeout(() => {
      const el = document.getElementById('variant-name-input') as HTMLInputElement;
      if (el) { el.focus(); el.select(); }
    }, 0);
  }

  async confirmRename() {
    const name = (this.variantNameInput || '').trim();
    const targetKey = this.variantNameInputTargetKey;
    if (!name || !targetKey) return;
    try {
      await this.reportService.renameVariant(targetKey, name);
      AppComponent.showSnack('Variant renamed.', 'OK', 3000);
      this.closeVariantsMenu();
    } catch (e: any) {
      AppComponent.showSnack(e?.error?.msg || e?.msg || 'Failed to rename variant.', 'OK', 4000);
    }
  }

  cancelNameInput() {
    this.variantNameInputMode = null;
    this.variantNameInput = '';
    this.variantNameInputTargetKey = null;
  }

  async deleteActiveVariant() {
    if (!this.activeVariantKey) return;
    const current = this.variants.find(v => v.key === this.activeVariantKey);
    if (!current) return;
    const dialogRef = AppComponent.showMessage(
      'Delete Variant',
      `Delete the variant "${current.name}"? This cannot be undone.`,
      ['Confirm', 'Cancel'],
      false,
    );
    dialogRef.afterClosed().subscribe(async (result: any) => {
      if (!result || result.value !== 'Confirm') return;
      try {
        await this.reportService.deleteVariant(current.key);
        this.closeVariantsMenu();
        // Navigate away from the now-deleted variant
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { variant: null },
          queryParamsHandling: 'merge',
        });
      } catch (e: any) {
        AppComponent.showSnack(e?.error?.msg || e?.msg || 'Failed to delete variant.', 'OK', 4000);
      }
    });
  }

  async setActiveAsDefault() {
    if (!this.activeVariantKey) return;
    try {
      await this.reportService.setDefaultVariant(this.activeVariantKey);
      AppComponent.showSnack('Default variant updated.', 'OK', 3000);
      this.closeVariantsMenu();
    } catch (e: any) {
      AppComponent.showSnack(e?.error?.msg || e?.msg || 'Failed to set default.', 'OK', 4000);
    }
  }

  async clearDefaultVariant() {
    try {
      await this.reportService.setDefaultVariant(null);
      AppComponent.showSnack('Default cleared.', 'OK', 3000);
      this.closeVariantsMenu();
    } catch (e: any) {
      AppComponent.showSnack(e?.error?.msg || e?.msg || 'Failed to clear default.', 'OK', 4000);
    }
  }

  // ─── List Builder ──────────────────────────────────────────

  toggleListBuilder() {
    if (this.listBuilderActive) {
      this.listBuilderService.closeListBuilder();
    } else {
      this.listBuilderService.openListBuilder(this.template.key);
    }
  }

  addToList(event, recordId) {
    event.stopPropagation();
    this.listBuilderService.addItem(recordId);
  }

  addAllToList() {
    const input = this.reportService.getLoadRecordsInput();
    this.listBuilderService.addAll(input.clientConfig, new Date().getTimezoneOffset());
  }

  removeAllFromList() {
    const input = this.reportService.getLoadRecordsInput();
    this.listBuilderService.removeAll(input.clientConfig, new Date().getTimezoneOffset());
  }

  isItemInList(itemId: string): boolean {
    return this.listBuilderService.isItemInList(itemId);
  }

  startResize(event: MouseEvent) {
    event.preventDefault();
    const hostEl = (event.target as HTMLElement).parentElement;
    if (!hostEl) return;

    const onMouseMove = (e: MouseEvent) => {
      const rect = hostEl.getBoundingClientRect();
      const percent = ((e.clientX - rect.left) / rect.width) * 100;
      const clamped = Math.max(20, Math.min(80, percent));
      this.reportFlexBasis = clamped + '%';
      this.changeDetectorRef.detectChanges();
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }
}
